import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "medical-seo-colorado",
  metaTitle: "Medical SEO in Colorado | Local SEO for Medical Practices & Clinics",
  metaDescription:
    "Medical SEO across Colorado — Map Pack rankings, new-patient growth, and HIPAA-aware content for primary care and specialty clinics. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Medical marketing", to: "/medical-marketing-colorado" },
  ],
  currentLabel: "Medical SEO Colorado",
  eyebrow: "Medical SEO — Colorado",
  h1: (
    <>
      Medical <span className="text-brand-gold">SEO</span> in Colorado
    </>
  ),
  heroDescription:
    "Win the searches that drive new-patient acquisition — primary care, dermatology, orthopedics, women's health, cardiology, and more — across every Colorado market.",
  intro:
    "Most medical SEO is generic — one 'doctor near me' page and a stock-photo hero. We build dedicated ranking pages for the specialties and conditions that actually grow practices: dermatology, orthopedics, sports medicine, women's health, primary care, cardiology, and more. Each page is FAQ-rich, schema-marked-up with MedicalBusiness and Physician, and tied into a properly tuned Google Business Profile that captures real patient intent.",
  definition: {
    term: "Medical SEO in Colorado",
    body: "Medical SEO is local search optimization that ranks medical practices for high-intent searches like 'dermatologist Denver', 'orthopedic surgeon Colorado Springs', or 'primary care doctor near me' — through Map Pack work, specialty-page content, provider profiles, and HIPAA-compliant review velocity.",
  },
  whatWeDo: [
    { title: "One ranking page per specialty or condition", body: "Primary care, dermatology, orthopedics, women's health, sports medicine, cardiology — each with its own FAQ-rich page." },
    { title: "Map Pack work in every Colorado market", body: "Colorado Springs, Denver, Fort Collins, Boulder, Castle Rock — neighborhood-deep targeting for new-patient capture." },
    { title: "Insurance-acceptance pages", body: "Dedicated content for major Colorado insurers (Anthem, UnitedHealthcare, Cigna, Kaiser, Tricare) so patients searching by plan find you first." },
    { title: "Provider profile pages", body: "Each provider gets a dedicated page with credentials, conditions treated, education, and Physician schema — strong for both Google and AI search." },
    { title: "HIPAA-compliant review velocity", body: "Review-request workflow tied to visit completion in your EMR — no PHI in the request itself, just compliant outreach to satisfied patients." },
    { title: "Schema for medical trust signals", body: "MedicalBusiness, Physician, MedicalCondition, Service, FAQPage, and Review schema — credentials, insurance, and conditions surfaced on-page." },
  ],
  whyThisMarket: [
    {
      heading: "Hospital systems and national directories own page one",
      body: "Most independent Colorado medical practices are buried under UCHealth, Centura, Healthgrades, and Zocdoc. Targeted specialty + neighborhood content is the only sustainable way to outrank them.",
    },
    {
      heading: "Patients search by symptom and specialty, not 'doctor near me'",
      body: "'Knee pain doctor Denver', 'pediatric dermatologist Colorado Springs', 'menopause specialist Boulder' — these are the searches with real new-patient intent. Most practice sites have zero pages targeting them.",
    },
    {
      heading: "Insurance-acceptance content is dramatically under-built",
      body: "Patients constantly search 'doctor that accepts Anthem near me' or 'primary care that takes Tricare'. Most Colorado practices bury this in a footer. Dedicated insurance-acceptance pages capture that intent directly.",
    },
  ],
  relatedLinks: [
    { to: "/medical-marketing-colorado", label: "Medical marketing in Colorado — full hub" },
    { to: "/colorado-springs-seo", label: "How our SEO process works" },
    { to: "/colorado-springs-google-maps", label: "Local SEO & Google Maps overview" },
    { to: "/dental-seo-colorado", label: "Dental SEO in Colorado" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/denver-digital-marketing", label: "Denver digital marketing" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/seo", label: "Our SEO service (all industries)" },
    { to: "/local-seo", label: "Local SEO overview" },
  ],
  faqs: [
    { q: "How long does medical SEO take to work in Colorado?", a: "Most Colorado medical practices see Map Pack movement in 90–150 days and stronger new-patient flow by month 4–6. Specialty practices tend to compound faster because procedure-specific intent is searchable and lower-competition than generic 'doctor' terms." },
    { q: "Should I focus on primary care or specialty content?", a: "Both, on different pages. Primary-care pages drive volume; specialty/procedure pages drive revenue and out-of-network value. We typically build a strong primary-care path with dedicated ranking pages for each specialty and high-value condition." },
    { q: "Are your medical review workflows HIPAA-compliant?", a: "Yes. We use HIPAA-compliant review platforms and configure them so no PHI flows into the request. Just compliant, systematic outreach to satisfied patients after visit completion." },
    { q: "Do you only serve Colorado Springs medical practices?", a: "No — we serve practices across all of Colorado: Denver, Fort Collins, Boulder, Pueblo, Castle Rock, Parker, and the Front Range corridor." },
  ],
  serviceName: "Medical SEO in Colorado",
  serviceDescription:
    "Local SEO and Map Pack optimization for medical practices across Colorado, with focus on specialty pages, provider profiles, and new-patient acquisition.",
  areaServed: "Colorado",
  // SEO: consolidate ranking signal onto the /medical-marketing-colorado flagship hub.
  canonicalOverride: "https://coloradowebimpressions.com/medical-marketing-colorado",
};

export const Route = createFileRoute("/medical-seo-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
